import type { FeedEvent } from "@/types/feed";

/** Known entity fields carried on feed events — used to mask embedded party names. */
export interface FeedPrivacyScope {
  consignee?: string;
  sales_person?: string;
  opened_by?: string;
  reference_id?: string | null;
  /** Party names and emails mined from structured insight / mail payloads. */
  extra?: string[];
}

const PAYLOAD_PARTY_KEYS = new Set([
  "sales_person",
  "consignee",
  "opened_by",
  "shipper",
  "shipper_name",
  "agent",
]);

const PAYLOAD_EMAIL_KEYS = new Set([
  "mailbox_email",
  "from_address",
  "from_email",
  "sender_email",
  "email",
]);

const PARTY_SCOPE_DIMENSIONS = new Set([
  "opened_by",
  "sales_person",
  "consignee",
  "agent",
]);

/** Insight list keys whose `label` is a route / carrier / state — not a party. */
const NON_PARTY_LABEL_LIST_KEYS = new Set([
  "top_lanes",
  "top_carriers",
  "by_source_type",
  "by_state",
  "by_carrier",
  "by_lane",
]);

/**
 * Event / dimension vocabulary that appears in titles (e.g. "Cut-off today")
 * and must never be treated as a party token.
 */
const FEED_VOCABULARY = new Set([
  "cutoff",
  "cutoffapproaching",
  "transshipment",
  "transhipment",
  "stuffing",
  "departing",
  "arriving",
  "delayed",
  "rolled",
  "rolleddelayed",
  "operational",
  "digest",
  "operationaldigest",
  "newbooking",
  "batchingested",
  "today",
  "yesterday",
  "tomorrow",
  "immediate",
  "developing",
  "strategic",
]);

const GENERIC_MAILBOX_LOCAL_PARTS = new Set([
  "info",
  "ops",
  "admin",
  "noreply",
  "no-reply",
  "mail",
  "office",
  "bookings",
  "sales",
  "support",
  "hello",
  "contact",
  "team",
  "desk",
  "notifications",
]);

export const FEED_EMAIL_RE =
  /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

export function looksLikeEmail(value: string): boolean {
  return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
    value.trim(),
  );
}

/** Compact form used to compare "Cut-off" / "cut_off" / "cutoff". */
export function compactPrivacyToken(value: string): string {
  return value.toLowerCase().replace(/[\s_/-]+/g, "");
}

export function isFeedPrivacyVocabulary(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return FEED_VOCABULARY.has(compactPrivacyToken(trimmed));
}

/** Person-like local-part of an email, when it is safe to mask as a name. */
export function emailLocalPart(value: string): string | null {
  if (!looksLikeEmail(value)) return null;
  const local = value.trim().split("@")[0] ?? "";
  if (!local) return null;
  if (GENERIC_MAILBOX_LOCAL_PARTS.has(local.toLowerCase())) return null;
  if (!/^[a-z][a-z0-9._-]{2,}$/i.test(local)) return null;
  return local;
}

function addToken(tokens: Set<string>, value: unknown) {
  if (typeof value !== "string") return;
  const trimmed = value.trim();
  if (!trimmed || isFeedPrivacyVocabulary(trimmed)) return;
  tokens.add(trimmed);
}

/** Walk insight/pattern payloads and collect party-name and email tokens. */
export function extractPayloadPartyTokens(payload: unknown): string[] {
  const tokens = new Set<string>();

  const walk = (node: unknown, listKey?: string) => {
    if (Array.isArray(node)) {
      node.forEach((item) => walk(item, listKey));
      return;
    }
    if (!node || typeof node !== "object") return;

    const record = node as Record<string, unknown>;
    const scope = record.scope;
    if (scope && typeof scope === "object" && !Array.isArray(scope)) {
      const dim = (scope as { dimension?: unknown }).dimension;
      const key = (scope as { key?: unknown }).key;
      if (
        typeof dim === "string" &&
        PARTY_SCOPE_DIMENSIONS.has(dim) &&
        typeof key === "string"
      ) {
        addToken(tokens, key);
      }
    }

    for (const [key, value] of Object.entries(record)) {
      if (PAYLOAD_PARTY_KEYS.has(key) || PAYLOAD_EMAIL_KEYS.has(key)) {
        addToken(tokens, value);
        continue;
      }
      if (
        key === "label" &&
        listKey &&
        (listKey.startsWith("top_") || listKey.startsWith("by_")) &&
        !NON_PARTY_LABEL_LIST_KEYS.has(listKey)
      ) {
        addToken(tokens, value);
        continue;
      }
      if (
        key.startsWith("top_") ||
        key.startsWith("by_") ||
        key === "metrics"
      ) {
        walk(value, key);
      }
    }
  };

  walk(payload);
  return [...tokens];
}

/** Build the privacy scope for a feed row from its entity tags, reference id, and payload. */
export function feedPrivacyScope(
  event: Pick<
    FeedEvent,
    "entity_scope" | "reference_id" | "reference_type" | "payload"
  >,
): FeedPrivacyScope {
  const bookingRef =
    event.reference_type === "booking" ? event.reference_id : null;

  return {
    consignee: event.entity_scope?.consignee,
    sales_person: event.entity_scope?.sales_person,
    opened_by: event.entity_scope?.opened_by,
    reference_id: bookingRef,
    extra: extractPayloadPartyTokens(event.payload),
  };
}
